import { adminFetch } from '../adminFetch';

/** Erreur d'un appel au catalogue : message lisible, et le code du portail. */
export class ErreurCatalogue extends Error {
  constructor(
    message: string,
    readonly code: string | undefined,
    readonly status: number,
  ) {
    super(message);
  }
}

/**
 * Appel à l'API du portail pour le catalogue (formules, fonctionnalités, codes,
 * produits), dans la forme qu'attendaient les écrans repris de SEmplyApp :
 * `call(path, { method, body })` → JSON, ou une `Error` au message lisible.
 *
 * L'autorisation est la SESSION du portail (cookie), relue par `AdminGuard` à
 * chaque requête — le jeton que les back-offices produits transmettaient n'a
 * plus lieu d'être ici.
 */
export async function catalogueCall(path: string, options: RequestInit = {}): Promise<unknown> {
  const res = await adminFetch(path, {
    ...options,
    headers: {
      ...(options.body && !(options.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers ?? {}),
    },
  });
  if (!res.ok) {
    const corps = (await res.json().catch(() => null)) as { error?: string; message?: unknown } | null;
    if (res.status === 401) {
      throw new ErreurCatalogue('Session expirée : reconnectez-vous.', 'unauthorized', 401);
    }
    if (corps?.error === 'superadmin_required') {
      throw new ErreurCatalogue('Réservé aux superadmins de la suite.', corps.error, res.status);
    }
    if (corps?.error === 'admin_amr_required') {
      throw new ErreurCatalogue(
        'Reconnectez-vous avec votre mot de passe et votre code à usage unique : le catalogue exige une connexion à deux facteurs.',
        corps.error,
        res.status,
      );
    }
    const message = Array.isArray(corps?.message)
      ? corps.message.join(' • ')
      : typeof corps?.message === 'string'
        ? corps.message
        : `Erreur ${res.status}`;
    throw new ErreurCatalogue(message, corps?.error, res.status);
  }
  return res.status === 204 ? null : res.json();
}
